import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { initVisualizer, applyTileImage, applyTimeValue, SUN_ANGLE } from './visualizer';
import Theme from '../../../themes/index';
import { connectLocation } from '../../location/LocationConnectors';
import IconDaylightDay from '../icons/icon-daylight-day';
import IconDaylightNight from '../icons/icon-daylight-night';

const Container = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const VisualizerFrame = styled.div`
  aspect-ratio: 1.5;
  position: relative;
  width: 100%;
`;

const VisualizerContainer = styled.div`
  aspect-ratio: 1.5;
  width: 100%;
`;

const LoadingOverlay = styled.div`
  aspect-ratio: 1.5;
  background: #ffffffaa;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 3;
`;

const Description = styled.div`
  color: ${Theme.colors.textGrey};
  display: flex;
  flex-direction: column;
  font-family: ${Theme.fonts.body};
  gap: 8px;
  padding: 16px 16px 0;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #000;
    font-family: ${Theme.fonts.body};
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 24px;
    text-transform: none;
  }

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }

  @media (min-width: 768px) {
    color: #000;
    gap: 16px;

    h2 {
      font-size: 28px;
      line-height: 28px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const DayNightSliderStyles = css`
  align-items: center;
  bottom: 14px;
  display: flex;
  gap: 8px;
  left: 8px;
  position: absolute;
  right: 8px;
  z-index: 2;

  svg {
    flex: 0 0 auto;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
  }

  input {
    appearance: none;
    accent-color: ${Theme.colors.brand};
    background: rgba(255, 255, 255, 0.65);
    border: 0;
    border-radius: 10px;
    cursor: pointer;
    direction: rtl;
    flex: 1 1 auto;
    height: 20px;
    margin: 0;
    min-width: 0;
    outline: none;

    &::-webkit-slider-thumb {
      appearance: none;
      background-color: ${Theme.colors.brand};
      background-image: linear-gradient(
        to right,
        transparent 7px,
        rgba(255, 255, 255, 0.75) 7px,
        rgba(255, 255, 255, 0.75) 8px,
        transparent 8px,
        transparent 11px,
        rgba(255, 255, 255, 0.75) 11px,
        rgba(255, 255, 255, 0.75) 12px,
        transparent 12px,
        transparent 15px,
        rgba(255, 255, 255, 0.75) 15px,
        rgba(255, 255, 255, 0.75) 16px,
        transparent 16px
      );
      background-position: center;
      background-repeat: no-repeat;
      background-size: 100% 10px;
      border: 0;
      border-radius: 8px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.28);
      height: 16px;
      width: 24px;
    }

    &::-moz-range-thumb {
      background-color: ${Theme.colors.brand};
      background-image: linear-gradient(
        to right,
        transparent 7px,
        rgba(255, 255, 255, 0.75) 7px,
        rgba(255, 255, 255, 0.75) 8px,
        transparent 8px,
        transparent 11px,
        rgba(255, 255, 255, 0.75) 11px,
        rgba(255, 255, 255, 0.75) 12px,
        transparent 12px,
        transparent 15px,
        rgba(255, 255, 255, 0.75) 15px,
        rgba(255, 255, 255, 0.75) 16px,
        transparent 16px
      );
      background-position: center;
      background-repeat: no-repeat;
      background-size: 100% 10px;
      border: 0;
      border-radius: 8px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.28);
      height: 16px;
      width: 24px;
    }

    &:focus-visible::-webkit-slider-thumb {
      outline: 2px solid white;
      outline-offset: 2px;
    }

    &:focus-visible::-moz-range-thumb {
      outline: 2px solid white;
      outline-offset: 2px;
    }
  }
`;

const zoomWrapperStyles = css`
  position: relative;
  cursor: pointer;

  img {
    display: block;
  }

  .fa {
    background: rgba(0, 0, 0, 0.5);
    border-bottom: 1px white solid;
    border-right: 1px white solid;
    color: #fff;
    font-size: 24px;
    left: 0;
    padding: 12px;
    position: absolute;
    top: 0;
  }
`;

const DayNightSlider = connectLocation(({ t, value, onChange }) => (
  <div css={DayNightSliderStyles}>
    <IconDaylightDay />
    <input
      aria-label={t('shingles.day_and_night_view')}
      type="range"
      min={SUN_ANGLE.dusk}
      max={SUN_ANGLE.dawn}
      step="any"
      value={value}
      onInput={onChange}
    />
    <IconDaylightNight />
  </div>
));

const VisualizerDescription = connectLocation(({ t }) => (
  <Description>
    <h2>{t('shingles.daylight_visualizer.title')}</h2>
    <p>{t('shingles.daylight_visualizer.description')}</p>
  </Description>
));

const ZoomWrapper = ({ children }) => (
  <div css={zoomWrapperStyles}>
    <span className="fa fa-search-plus" />
    {children}
  </div>
);

export const DEFAULT_DAY_NIGHT_VALUE = SUN_ANGLE.midday;

export const DayNightVisualizer = ({
  tileImageUrl,
  shingleLineSlug,
  shingleColorUid,
  showZoom,
  onImageClick,
  dayNightValue,
  onDayNightValueChange,
  showDescription,
}) => {
  const visualizerContainerRef = useRef();
  const loadingOverlayRef = useRef();
  const latestTileImageRef = useRef({ tileImageUrl, shingleLineSlug, shingleColorUid });
  const [isVisualizerAvailable, setVisualizerAvailable] = useState(true);
  const [isVisualizerInitialized, setVisualizerInitialized] = useState(false);
  const [internalDayNightValue, setInternalDayNightValue] = useState(Number(dayNightValue));

  latestTileImageRef.current = { tileImageUrl, shingleLineSlug, shingleColorUid };

  useEffect(() => {
    let cleanupVisualizer;
    const initializeVisualizer = () => {
      const latestTileImage = latestTileImageRef.current;
      cleanupVisualizer = initVisualizer(
        visualizerContainerRef.current,
        loadingOverlayRef.current,
        latestTileImage.tileImageUrl,
        latestTileImage.shingleLineSlug,
        latestTileImage.shingleColorUid
      );
      if (!cleanupVisualizer) {
        setVisualizerAvailable(false);
        return;
      }
      setVisualizerInitialized(true);
    };
    const canRequestIdleCallback = typeof window.requestIdleCallback === 'function';
    const idleCallbackId = canRequestIdleCallback
      ? window.requestIdleCallback(initializeVisualizer, { timeout: 1000 })
      : window.setTimeout(initializeVisualizer, 0);

    // Renderer setup runs once; the following effect handles subsequent texture changes.
    return () => {
      if (canRequestIdleCallback) {
        window.cancelIdleCallback?.(idleCallbackId);
      } else {
        window.clearTimeout(idleCallbackId);
      }
      cleanupVisualizer?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyTileImage(tileImageUrl, shingleLineSlug, shingleColorUid);
  }, [tileImageUrl, shingleLineSlug, shingleColorUid]);

  useEffect(() => {
    setInternalDayNightValue(Number(dayNightValue));
  }, [dayNightValue]);

  useEffect(() => {
    if (isVisualizerInitialized) applyTimeValue(internalDayNightValue);
  }, [internalDayNightValue, isVisualizerInitialized]);

  const onDayNightChangeHandler = event => {
    const nextValue = Number(event.currentTarget.value);
    setInternalDayNightValue(nextValue);
    onDayNightValueChange?.(nextValue);
  };

  if (!isVisualizerAvailable) return null;

  return (
    <Container>
      <VisualizerFrame>
        {showZoom ? (
          <ZoomWrapper>
            <VisualizerContainer ref={visualizerContainerRef} onClick={onImageClick} />
          </ZoomWrapper>
        ) : (
          <VisualizerContainer ref={visualizerContainerRef} onClick={onImageClick} />
        )}
        <DayNightSlider value={internalDayNightValue} onChange={onDayNightChangeHandler} />
        <LoadingOverlay ref={loadingOverlayRef} />
      </VisualizerFrame>
      {showDescription && <VisualizerDescription />}
    </Container>
  );
};

DayNightVisualizer.propTypes = {
  dayNightValue: PropTypes.number,
  onDayNightValueChange: PropTypes.func,
  onImageClick: PropTypes.func,
  shingleLineSlug: PropTypes.string,
  shingleColorUid: PropTypes.string,
  showDescription: PropTypes.bool,
  showZoom: PropTypes.bool,
  tileImageUrl: PropTypes.string,
};

DayNightVisualizer.defaultProps = {
  dayNightValue: DEFAULT_DAY_NIGHT_VALUE,
  onDayNightValueChange: null,
  onImageClick: null,
  shingleLineSlug: null,
  shingleColorUid: null,
  showDescription: false,
  showZoom: false,
  tileImageUrl: null,
};

export default DayNightVisualizer;
